import { lazy, Suspense } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import AuthCheck from './shared/components/AuthCheck';
import ErrorBoundary from './shared/components/ErrorBoundary';
import { PageErrorFallback } from './shared/components/ErrorFallback';
import RouteTracker from './shared/components/RouteTracker';

// Lazy load all route components for better performance
const Homepage = lazy(() => import("./customer/pages/Homepage"));
const Login = lazy(() => import("./customer/components/Login"));
const Signup = lazy(() => import("./customer/components/Signup"));
const Cart = lazy(() => import("./customer/pages/Cart"));
const Address = lazy(() => import("./customer/pages/Address"));
const Orders = lazy(() => import("./customer/pages/Orders"));
const OrderDetails = lazy(() => import("./customer/pages/OrderDetails"));
const CreateOrder = lazy(() => import("./customer/pages/CreateOrder"));
const Profile = lazy(() => import("./customer/pages/Profile"));
const AddressManagement = lazy(() => import("./customer/pages/AddressManagement"));
const About = lazy(() => import("./shared/pages/About"));
const ContactUs = lazy(() => import("./shared/pages/ContactUs"));
const ChangeProfile = lazy(() => import("./shared/pages/ChangeProfile"));
const SellerLayout = lazy(() => import("./seller/pages/SellerLayout"));
const SellerDashboard = lazy(() => import("./seller/pages/SellerDashboard"));
const SellerProductsSection = lazy(() => import("./seller/components/SellerProductsSection"));
const SellerUploadSection = lazy(() => import("./seller/components/SellerUploadSection"));
const SellerInventorySection = lazy(() => import("./seller/components/SellerInventorySection"));
const SellerShipmentsSection = lazy(() => import("./seller/components/SellerShipmentsSection"));
const ComingSoon = lazy(() => import("./seller/components/ComingSoon"));
const ProductDetails = lazy(() => import("./customer/pages/ProductDetails"));
const Products = lazy(() => import("./customer/pages/Products"));
const SearchResults = lazy(() => import("./customer/pages/SearchResults"));
const PrivacyPolicy = lazy(() => import("./shared/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./shared/pages/TermsOfService"));
const KarigarPrivacyPolicy = lazy(() => import("./shared/pages/KarigarPrivacyPolicy"));
const KarigarDeleteAccount = lazy(() => import("./shared/pages/KarigarDeleteAccount"));
const Logistics = lazy(() => import("./logistics/pages/Logistics"));
const AssignDelivery = lazy(() => import("./logistics/pages/AssignDelivery"));
const AdminDashboard = lazy(() => import("./admin/pages/AdminDashboard"));
const AdminBillingDashboard = lazy(() => import("./admin/pages/AdminBillingDashboard"));
const ForgotPassword = lazy(() => import("./shared/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./shared/pages/ResetPassword"));
const DeliveryOrders = lazy(() => import("./delivery/pages/DeliveryOrders"));
const DeliveryPersons = lazy(() => import("./logistics/pages/DeliveryPersons"));
const DeliveryDashboard = lazy(() => import("./delivery/pages/DeliveryDashboard"));
const DeliveryLayout = lazy(() => import("./delivery/pages/DeliveryLayout"));
const HireKarigar = lazy(() => import("./customer/pages/HireKarigar"));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '1.2rem',
    color: '#666',
    flexDirection: 'column',
    gap: '1rem'
  }}>
    <div className="spinner" style={{
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #3498db',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      animation: 'spin 1s linear infinite'
    }} />
    <span>Loading...</span>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

function App() {
  return (
    <ErrorBoundary fallback={<PageErrorFallback />} name="root">
      <BrowserRouter>
        <RouteTracker />
            <ToastContainer />
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
          {/* Public Routes */}
          <Route index element={<Homepage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/change-profile" element={<ChangeProfile />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:productId" element={<ProductDetails />} />
          <Route path="/search" element={<SearchResults />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/karigar/privacy-policy" element={<KarigarPrivacyPolicy />} />
          <Route path="/karigar/delete-account" element={<KarigarDeleteAccount />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/hire-karigar" element={<HireKarigar />} />
          
          {/* Protected Routes */}
          <Route path="/cart" element={
            <AuthCheck>
              <Cart />
            </AuthCheck>
          } />
          <Route path="/address" element={
            <AuthCheck>
              <Address />
            </AuthCheck>
          } />
          <Route path="/address-management" element={
            <AuthCheck>
              <AddressManagement />
            </AuthCheck>
          } />
          <Route path="/orders" element={
            <AuthCheck>
              <Orders />
            </AuthCheck>
          } />
          <Route path="/orders/:orderId" element={
            <AuthCheck>
              <OrderDetails />
            </AuthCheck>
          } />
          <Route path="/profile" element={
            <AuthCheck>
              <Profile />
            </AuthCheck>
          } />
          <Route path="/create-order" element={
            <AuthCheck>
              <CreateOrder />
            </AuthCheck>
          } />
          <Route path="/seller" element={
            <AuthCheck requiredRoles={['SELLER']}>
              <SellerLayout />
            </AuthCheck>
          }>
            <Route index element={<SellerDashboard />} />
            <Route path="products" element={<SellerProductsSection />} />
            <Route path="upload" element={<SellerUploadSection />} />
            <Route path="inventory" element={<SellerInventorySection />} />
            <Route path="shipments" element={<SellerShipmentsSection />} />
            <Route path="udhaar-book" element={<ComingSoon title="Udhaar Book" description="Credit management feature is coming soon. Track customer credit entries and manage payment records." icon="📖" />} />
            <Route path="udhaar-history" element={<ComingSoon title="Udhaar History" description="View and search customer credit history. This feature will be available soon." icon="📊" />} />
          </Route>
          
          <Route path="/logistics" element={
            <AuthCheck requiredRoles={['LOGISTICS']}>
              <Logistics />
            </AuthCheck>
          } />
          <Route path="/logistics/assign-delivery/:orderId" element={
            <AuthCheck requiredRoles={['LOGISTICS']}>
              <AssignDelivery />
            </AuthCheck>
          } />
          <Route path="/logistics/delivery-persons" element={
            <AuthCheck requiredRoles={['LOGISTICS']}>
              <DeliveryPersons />
            </AuthCheck>
          } />

          <Route path="/delivery" element={
            <AuthCheck requiredRoles={['DELIVERY']}>
              <DeliveryLayout />
            </AuthCheck>
          }>
            <Route index element={<DeliveryDashboard />} />
            <Route path="orders" element={<DeliveryOrders />} />
          </Route>
          <Route path="/admin" element={
            <AuthCheck requiredRoles={['ADMIN']}>
              <AdminDashboard />
            </AuthCheck>
          } />
          <Route path="/admin/billing" element={
            <AuthCheck requiredRoles={['ADMIN']}>
              <AdminBillingDashboard />
            </AuthCheck>
          } />
              </Routes>
            </Suspense>
        </BrowserRouter>
      </ErrorBoundary>
  );
}

export default App;
